import { useState } from "react";

import { downloadRawFromSharePoint } from "../../services/documentPreviewService";
import { acquireDelegatedGraphToken } from "../../utils/msalTokenService";
import { type DocumentData, filenameOf, sharePointError } from "./documentPreviewTypes";
import { type DocumentPreviewLoad } from "./useDocumentPreview";

/** Give the browser a moment to start the download before releasing the URL. */
const REVOKE_DELAY_MS = 1000;

export interface DocumentActions {
  /** True when the document can be opened outside DealOS. */
  canOpen: boolean;
  opening: boolean;
  downloadingRaw: boolean;
  download: () => void;
  downloadRaw: () => Promise<void>;
  openExternally: () => Promise<void>;
}

function triggerDownload(url: string, filename: string): void {
  const link = window.document.createElement("a");
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
}

async function sharePointWebUrl(doc: DocumentData): Promise<string | undefined> {
  const graphToken = await acquireDelegatedGraphToken();
  const graphUrl = `https://graph.microsoft.com/v1.0/sites/${doc.sharepointSiteId}/drives/${doc.sharepointDriveId}/items/${doc.sharepointItemId}?$select=webUrl`;
  const res = await fetch(graphUrl, { headers: { Authorization: `Bearer ${graphToken}` } });
  if (!res.ok) throw new Error(`Graph API returned ${res.status}`);

  const data = await res.json();
  return data.webUrl;
}

/** Opens the document in SharePoint, or straight from its URL when it has one. */
function useOpenExternally(
  document: DocumentData | null,
  hasDirectUrl: boolean,
  hasSharePointIds: boolean
): { opening: boolean; openExternally: () => Promise<void> } {
  const [opening, setOpening] = useState<boolean>(false);

  const openExternally = async (): Promise<void> => {
    if (!document) return;

    if (hasDirectUrl) {
      window.open(document.filePath, "_blank", "noopener,noreferrer");
      return;
    }
    if (!hasSharePointIds) return;

    setOpening(true);
    try {
      const webUrl = await sharePointWebUrl(document);
      if (webUrl) {
        window.open(webUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Failed to get SharePoint web URL:", err);
    } finally {
      setOpening(false);
    }
  };

  return { opening, openExternally };
}

/** Downloading and opening the previewed document. */
export function useDocumentActions(
  document: DocumentData | null,
  preview: DocumentPreviewLoad
): DocumentActions {
  const [downloadingRaw, setDownloadingRaw] = useState<boolean>(false);

  const hasSharePointIds = Boolean(
    document?.sharepointSiteId && document?.sharepointDriveId && document?.sharepointItemId
  );
  const hasDirectUrl = Boolean(document?.filePath && /^https?:\/\//i.test(document.filePath));
  const { opening, openExternally } = useOpenExternally(document, hasDirectUrl, hasSharePointIds);

  const download = (): void => {
    if (preview.objectUrl && document) {
      triggerDownload(preview.objectUrl, filenameOf(document));
    }
  };

  /** Fallback for documents Graph cannot convert to PDF: hand over the raw file. */
  const downloadRaw = async (): Promise<void> => {
    if (!document || downloadingRaw) return;

    setDownloadingRaw(true);
    try {
      const blob: Blob = await downloadRawFromSharePoint(document);
      const url: string = URL.createObjectURL(blob);
      triggerDownload(url, filenameOf(document));
      setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
    } catch (err) {
      console.error("Failed to download raw file from SharePoint:", err);
      preview.setDownloadOnly(false);
      preview.setError(
        sharePointError(err) || {
          message: "Failed to download file from SharePoint",
          type: "generic",
        }
      );
    } finally {
      setDownloadingRaw(false);
    }
  };

  return {
    canOpen: hasDirectUrl || hasSharePointIds,
    opening,
    downloadingRaw,
    download,
    downloadRaw,
    openExternally,
  };
}
